import { LatLng, LatLngBounds } from "leaflet";

export const CURRENT_VERSION = 7;

export function isString(v: any): boolean {
	return typeof v === 'string' || v instanceof String;
}

export function isB(bp: AufnahmePunktDocument): boolean {
	return idOf(bp?.type) === 6 || idOf(bp?.type) === 9991;
}

export function isG(bp: AufnahmePunktDocument): boolean {
	return idOf(bp?.type) >= 1 && idOf(bp?.type) <= 4;
}

export function isM(bp: AufnahmePunktDocument): boolean {
	return idOf(bp?.type) === 7;
}

export function isTorf(z: SubstratZeile): boolean {
	// Nur die überwiegende Torfart (erste Spalte) prüfen
	return z.torfarten?.[0]?.startsWith('h') ?? false;
}

export function isMudde(z: SubstratZeile): boolean {
	// Nur die überwiegende Torfart (erste Spalte) prüfen
	return z.torfarten?.[0]?.startsWith('y') ?? false;
}

export function isQuellsediment(z: SubstratZeile): boolean {
	// Nur die überwiegende Torfart (erste Spalte) prüfen
	return z.torfarten?.[0]?.startsWith('q') ?? false;
}

export function isOrganischesSubstrat(z: SubstratZeile): boolean {
	const t = z.torfarten?.[0];
	if (!t) return false; // Keine Torfart → mineralisch (reines Substrat)
	if (t.startsWith('h')) return true;      // Torf
	if (t.startsWith('y-o')) return true;    // Organomudde
	if (t.startsWith('y-c')) return true;    // Kalkmudde
	if (t === 'q-c' || t === 'q-o') return true; // Quellkalk, org. Quellsediment
	return false; // y-s* (Silikatmudde), q-s, etc. → mineralisch
}

/**
 * Berechnet die Moormächtigkeit (Gesamttiefe der organischen Substrate).
 * - Mineralische Schicht ≥40 cm: deren Obergrenze = Moormächtigkeit
 * - Mineralische Schicht <40 cm: zählt mit rein, Suche geht weiter
 * - Kein mächtiges mineralisches Substrat: Untergrenze des letzten organischen Substrats
 */
export function moorMaechtigkeit(substratfolge: SubstratZeile[]): number | undefined {
	if (!substratfolge?.length) return undefined;

	let letzteOrganischeTiefe: number | undefined;

	for (const z of substratfolge) {
		if (isOrganischesSubstrat(z)) {
			letzteOrganischeTiefe = z.tiefe;
		} else {
			// Mineralisches Substrat
			if (z.maechtigkeit >= 40) {
				// Obergrenze = Tiefe minus Mächtigkeit
				return z.tiefe - z.maechtigkeit;
			}
			// <40 cm: zählt mit rein, weiter suchen
		}
	}

	return letzteOrganischeTiefe;
}

/**
 * Generiert Moor-FBF-Vorschläge für das Typeahead.
 * Wahrscheinliche Kombinationen (z.B. ph→Ri, vh→Fe, ih→EF/Ml) stehen vorne.
 */
export function generateMoorFBFSuggestions(): string[] {
	const substrate = ['ah','akah','kah','ih','akih','kih','vh','akvh','kvh','ph','akph','d','akd','kd','q','akq','kq','mk','k','im','akim','kim','vm','akvm'];
	const bodenformen = ['Ri','Fe','EF','Ml'];
	const naehrkraft = ['R','K','M','MC','Z','ZC','A','AC','D'];

	// Wahrscheinliche Bodenform pro Substrat (kommt zuerst)
	const likely: Record<string, string[]> = {
		'ph': ['Ri'], 'akph': ['Ri'],
		'vh': ['Fe'], 'akvh': ['Fe'], 'kvh': ['Fe'],
		'ih': ['EF','Ml'], 'akih': ['EF','Ml'], 'kih': ['EF','Ml'],
		'ah': ['Ri','Fe'], 'akah': ['Ri','Fe'], 'kah': ['Ri','Fe'],
	};

	const prioritized: string[] = [];
	const rest: string[] = [];

	for (const s of substrate) {
		const likelyBf = likely[s] || [];
		for (const bf of bodenformen) {
			for (const nk of naehrkraft) {
				const fbf = `${s}${bf}.${nk}`;
				if (likelyBf.includes(bf)) {
					prioritized.push(fbf);
				} else {
					rest.push(fbf);
				}
			}
		}
	}

	return [...prioritized, ...rest];
}

export function istLeer(a: any | undefined): boolean {
	return a === undefined || a === null || a === "";
}

export function effNum(a: number): number | undefined {
	return isNaN(a) ? undefined : a;
}

export function dateString(dateStr: string | undefined): string {
	if (!dateStr) return '';
	const date = new Date(dateStr);
	const str = date?.toISOString()?.split('T')[0];
	return str;
}

export function dateUTCString(dateStr: string|number): string {
  return new Date(dateStr).toUTCString();
}
	
export function commentTimeString(time: number) {
	return (new Date(time)).toLocaleString();
}

export function trackByIndex(index: number, obj: any) {
	return index;
}
export function bodenformLabelString(bp: AufnahmePunktDocument): string {
  if (!bp || !bp.header) return '';
  const hasFbf = bp.header.bodenform?.bf?.some((b: BodenformPair) => !!b.fbf);
  const bf = hasFbf ? bodenformString(bp.header.bodenform, 'fbf') : bodenformString(bp.header.bodenform, 'hbf', true);
  const ws = wasserformString(bp, true, true);  // <-- true für filter7
  const mk = bp.extendedHeader?.mesoklima ? `-${bp.extendedHeader.mesoklima}` : '';
  const sf = bp.header.substratFeuchte ? `-${bp.header.substratFeuchte}` : '';
  return `${bf}${ws}${mk}${sf}`;
}

export function hatHumusForm(bp: AufnahmePunktDocument): boolean {
	return idOf(bp.type) !== 7;
}

export function hatVegetationsformengruppe(bp: AufnahmePunktDocument): boolean {
	return idOf(bp.type) === 7;
}

export function humusformLabel(bp: AufnahmePunktDocument): string {
	if (hatHumusForm(bp) && hatVegetationsformengruppe(bp))
		return "HF/VegFG";
	else if (hatHumusForm(bp))
		return "HF";
	else if (hatVegetationsformengruppe(bp))
		return "VegFG";
	else 
		return "";
}

export function bodenformString(bf: Bodenform|undefined, which: string, withPrefixSuffix = false) {
	const includePrefixSuffix = which === 'fbf' || withPrefixSuffix;
	let result = (includePrefixSuffix && !!bf?.prefix) ? bf.prefix : '';
	bf?.bf?.forEach((bf: BodenformPair, index: number) => {
		if (!(bf as any)[which]) return;
		if (index > 0) result += ' ' + bf.trenner;
		result += (result.length > 0 ? ' ' : '') + (bf as any)[which];
	});
	if (includePrefixSuffix && !!bf?.suffix) result += ' ' + bf.suffix;

	if (which === 'fbf')
		result = result.replaceAll(' ', '');

	return result;
}

function toSuperscript(s: string | number | undefined | null): string {
  if (s === undefined || s === null) return '';
  const str = String(s);
  return str.replace(/\+/g, '⁺').replace(/x/gi, 'ˣ');
}

export function wasserformString(bp: AufnahmePunktDocument, compact = false, filter7 = false): string {
  if (!bp?.header) {
    return '';
  }
    // 7er rausfiltern nur wenn filter7 = true (Vergleich mit == für Kompatibilität mit alten number-Daten)
  const wf1 = filter7 && bp.header.wasserstufe == '7' ? null : bp.header.wasserstufe;
  const wf2 = filter7 && bp.header.wasserstufe2 == '7' ? null : bp.header.wasserstufe2;

  if (!wf1 && !wf2) {
    return '';
  }
  if (wf1 && wf2) {
    const sp = compact ? '' : ' ';
    return `${toSuperscript(wf2)}${sp}/${sp}${toSuperscript(wf1)}`;
  }
  return toSuperscript(wf1 || wf2 || '');
}

export enum LocationSystem {
	EPSG25833 = 'EPSG:25833',
	EPSG25832 = 'EPSG:25832',
	EPSG4326 = 'EPSG:4326',
	EPSG4258 = 'EPSG:4258',
	EPSG3034 = 'EPSG:3034',
	EPSG3035 = 'EPSG:3035',
	EPSG3044 = 'EPSG:3044',
	EPSG3045 = 'EPSG:3045',
	EPSG3857 = 'EPSG:3857',
	EPSG4839 = 'EPSG:4839',
	EPSG5650 = 'EPSG:5650',
	EPSG4647 = 'EPSG:4647',
	// Gauß-Krüger Zonen
	EPSG31466 = 'EPSG:31466', // GK Zone 2
	EPSG31467 = 'EPSG:31467', // GK Zone 3
	EPSG31468 = 'EPSG:31468', // GK Zone 4
	EPSG31469 = 'EPSG:31469'  // GK Zone 5
}

export interface AltNeu<T> {
	alt?: T, neu?: T
}

export interface Location {
	system: LocationSystem,
	x: number, y: number
}

export interface ForstAdresse {
	gemarkung: string,
	flur?: number,
	flurstueck?: string,
	abteilung: string,
	unterabteilung: string,
	teilflaeche: string,
	hilfsflaeche?: string
}

export interface Geologie {
	oben: string,
	trenner?: '/' | '//',
	unten?: string
}

export interface Aufnahme {
	datum: string,
	kartierer: string,
	tiefe?: number
}

// Projekt-Zuordnung für Bohrpunkte
export interface ProjectAssignment {
	id: string;       // Projekt-Dokument _id
	name: string;     // Projektname (für Filterung ohne Lookup)
}

export interface Header {
	nummer: string,
	forstamt: string,
	forstbetrieb?: string,
	revier: string,
	adresse: ForstAdresse,
	koordinaten: Location,

	aufnahme: Aufnahme,
	bodenform: Bodenform,
	humusform: HumusForm,
	wasserstufe?: string, // Grundwasserstufe, steht hinten //
	wasserstufe2?: string, //Stauwasserstufe, sthet vorn //
	vegetationsformengruppe: string,

	substratFeuchte: string,

	bemerkungen: string,

	moorMaechtigkeit?: number,

	// Projekt-Zuordnungen (0-n)
	projects?: ProjectAssignment[],

	labornummer?: string,
	statusLaboranalyse?: '' | 'noch nicht ausgewertet' | 'Ergebnis liegt vor',
}

// Neigungsstufen nach SEA-Klassifikation
export type Neigungsstufe =
	| ''
	| 'Ebene'                          // ≤ 1°
	| 'schwächer geneigter Flachhang'  // > 1...3° (Rampe)
	| 'stärker geneigter Flachhang'    // > 3...7°
	| 'schwächer geneigter Lehnhang'   // > 7...15°
	| 'stärker geneigter Lehnhang'     // > 15...25°
	| 'schwächer geneigter Steilhang'  // > 25...60°
	| 'stärker geneigter Steilhang';   // > 60°

// Wölbungstypen (vertikal x horizontal)
export type Woelbung =
	| ''
	| 'xx'  // doppelt konvex
	| 'xg'  // konvex-gestreckt
	| 'xv'  // konvex-konkav
	| 'gx'  // gestreckt-konvex
	| 'gg'  // doppelt gestreckt
	| 'gv'  // gestreckt-konkav
	| 'vx'  // konkav-konvex
	| 'vg'  // konkav-gestreckt
	| 'vv'; // doppelt konkav

// Einbindung für Ebenen/Rampen
export type EinbindungEbene =
	| ''
	| 'Weitlage'
	| 'Horstlage'
	| 'Rückenlage'
	| 'Riedellage'
	| 'Schulterlage'
	| 'Simslage'
	| 'Fußlage'
	| 'Nischenlage'
	| 'Senkenlage'
	| 'Strecksenkenlage'
	| 'Tallage';

// Hanganschluss für stärker geneigte Hänge
export type Hanganschluss =
	| ''
	| 'B-Lage'   // Böschungslage (Ebene-Ebene)
	| 'V-Lage'   // Vollrelieflage (Kuppe-Ebene)
	| 'H-Lage'   // Hohlrelieflage (Ebene-Senke)
	| 'D-Lage';  // Doppellage (Kuppe-Senke)

// Hangteil bei größerer Höhenspanne
export type Hangteil =
	| ''
	| 'Oberhang'
	| 'Mittelhang'
	| 'Unterhang';

// Innere Rauhheit
export type InnereRauhheit =
	| ''
	| 'glatt'
	| 'leicht bewegt'
	| 'kleinwellig'
	| 'mittelwellig'
	| 'großwellig'
	| 'kupstig'
	| 'küstendünig'
	| 'strandwalldünig'
	| 'altdünig'
	| 'kleinkuppig'
	| 'mittelkuppig'
	| 'großkuppig'
	| 'kleinbucklig'
	| 'mittelbucklig'
	| 'großbucklig'
	| 'kleingieserartig'
	| 'mittelgieserartig'
	| 'ungeordnet versteilt'
	| 'horizontal gestuft'
	| 'hangfaltig'
	| 'hangfurchig';

// Hangrichtung (16 Kompass-Richtungen)
export type Hangrichtung =
	| ''
	| 'N' | 'NNO' | 'NO' | 'ONO'
	| 'O' | 'OSO' | 'SO' | 'SSO'
	| 'S' | 'SSW' | 'SW' | 'WSW'
	| 'W' | 'WNW' | 'NW' | 'NNW';

export interface Relief {
	// Basis-Relief (B-Punkte, Legacy)
	relief?: string,
	reliefUmgebung?: string,

	// Moor-Punkte
	reliefBestand?: string,
	reliefBodenvegetation?: string,

	// Erweiterte Relief-Attribute nach SEA
	neigungsstufe?: Neigungsstufe,
	neigungGrad?: number,              // Exakter Neigungswert in Grad
	woelbung?: Woelbung,
	hangrichtung?: Hangrichtung,

	// Einbindung in Nachbarschaft
	einbindungEbene?: EinbindungEbene, // Für Ebenen und Rampen
	hanganschluss?: Hanganschluss,     // Für stärker geneigte Hänge
	hangteil?: Hangteil,               // Ober-/Mittel-/Unterhang
	hoehenspanne?: number,             // Höhenspanne des Gesamthangs in m

	// Innere Rauhheit
	innereRauhheit?: InnereRauhheit
}

export interface HumusAuflage {
	ol?: number,
	of?: number,
	oh?: number
}

export interface HumusForm {
	aufnahmeFlaeche: AltNeu<string>,
	gesamtFlaeche?: AltNeu<string>,
	morphologisch?: AltNeu<string>
}

export interface StandortFormenGruppe {
	naehrkraftStufe: string,
	stammFeuchteStufe: Pair<string>,
	substratGruppe: string,
	zustands?: AltNeu<string>,
	imissions?: AltNeu<string>
}

export interface MosaikBereich {
	nummer: string,
	name: string,
	haupttyp: string
}

export interface Pair<T> {
	first: T,
	second: T
}

export interface Baumart {
	art: string,
	alter: number,
	hoehe: Pair<number>,
	durchmesser: Pair<number>,

	Bonitaet: number,
	qualitaet: string
}

export interface MoorTyp {
	oekologisch: string,
	hydrogenetisch: string
}

export interface BodenformPair {
	trenner: '/'|'//'|'',
	hbf: string,
	fbf: string
}

export interface Bodenform {
	bf: BodenformPair[],
	suffix?: string,
	prefix?: string
}

// Rohdaten der Wetterdaten für spätere Neuinterpretation
export interface WitterungRohdaten {
	temperature: number;        // Durchschnittstemperatur in °C
	precipitation: number;      // Niederschlag in mm (Tag)
	cloudCover: number;         // Bewölkung in %
	weatherCode: number;        // WMO Wettercode
	windSpeed: number;          // Windgeschwindigkeit in km/h
	windDirection: number;      // Windrichtung in Grad
	humidity: number;           // Relative Luftfeuchtigkeit in %
	precipitationDays: number;  // Niederschlag über konfigurierte Tage in mm
	daysUsed: number;           // Anzahl Tage für Niederschlagsberechnung
	fetchedAt?: string;         // Zeitpunkt des Abrufs (ISO-String)
	isCurrentWeather?: boolean; // true = aktuelles Wetter, false = historisch
}

export interface ExtendedHeader {
	geologie: Geologie,
	morphologie: string[],

	grundgesteinSediment?: string,
	relief: Relief,

	witterung?: string,
	witterungRohdaten?: WitterungRohdaten,

	klimaStufe: AltNeu<'' | 'f' | 'lf' | 'lm' | 'm' | 't'>,

	klimabereich?: string,
	wuchsgebiet?: string,
	wuchsbezirk?: string,

	humusAuflage: HumusAuflage,
	bestockungsTyp?: AltNeu<string>,

	stfg: StandortFormenGruppe
	mosaikBereich?: MosaikBereich

	standortklimatischeAngaben?: string,
	mesorelief?: string,
	lage?: string,

	hangrichtung?: 'N' | 'NNO' | 'NO' | 'ONO' | 'O' | 'OSO' | 'SO' | 'SSO' | 'S' | 'SSW' | 'SW' | 'WSW' | 'W' | 'WNW' | 'NW' | 'NNW',
	hangneigung?: number,
	meereshoehe?: number,

	vorbestandlicheGeschichte?: string,
	forstlicheBodenbearbeitung?: string,
	anthropogeneBeeinflussung?: string,
	duengemittelArt?: string,
	herbizide?: string,
	insektizide?: string,
	grundStauwasserVeraenderungen?: string,

	baumarten: Baumart[] | string,

	begruendungsart?: string,
	mischform?: string,
	waldfunktion?: string,

	entwaesserungsverhalten: ''|'I'|'II'|'III',

	kMgCaPSerie: string,
	vWertSaugsaum?: number,
	vWertSaugsaumVermutet?: '<15' | '15-40' | '>40',

	moorTyp?: MoorTyp,

	nWsk: number,
	nWskStufe: string,

	saugsaum?: number,
	karbonat?: number,
	spiegelTiefe?: number,
	mesoklima: ''|'fr'|'tr',

	gHorizont: {
			go: string, gr: string, g: string
	},

	sHorizont: { sw: string, sd: string, s: string },

	ph?: {
		wert1?: number,
		ort1?: '' | 'Oberboden' | 'Saugsaum',
		wert2?: number,
		ort2?: '' | 'Oberboden' | 'Saugsaum',
		durchschnitt?: number
	}
}

export interface BodenVegetation {
	// bestandsBeschreibung: string,
	groesseDerAufnahmeFlaeche?: string,
	aufnahme: BodenVegetationsAufnahme
}

export interface BodenVegetationsAufnahme {
	schichtAnteile: SchichtAnteile,
	vegetationen: BodenvegetationZeile[]
}

export interface SchichtAnteile {
	b1?: number,
	b2?: number,
	str?: number,
	kr1?: number,
	kr2?: number,
	m?: number
}

export interface BodenvegetationZeile {
	schicht?: 'b1'|'b2'|'str'|'kr1'|'kr2'|'m',
	haeufigkeit: string,
	vegetation: string,
}
	
export function schichtLabel(schicht: 'b1'|'b2'|'str'|'kr1'|'kr2'|'m'): string {
	switch (schicht) {
		case 'b1': return 'Baumschicht 1';
		case 'b2': return 'Baumschicht 2';
		case 'str': return 'Strauchschicht';
		case 'kr1': return 'Krautschicht 1';
		case 'kr2': return 'Krautschicht 2';
		case 'm': return 'Moosschicht';
		default: return schicht;
	}
}

export type Punktart = string;

export function idOf(punktart: string) {
	return parseInt(punktart?.substring(0, punktart?.indexOf('_')));
}

// Mapping von SYMBOL-ID zu Punktart-String (für Import aus GPKG/GeoJSON)
const SYMBOL_TO_PUNKTART: Record<number, string> = {
	1: '1_Weisergrube_ohne_Bodenanalyse',
	2: '2_Weisergrube_mit_Bodenanalyse',
	3: '3_Bodengrube_ohne_Bodenanalyse',
	4: '4_Bodengrube_mit_Bodenanalyse',
	5: '5_Humusprobe',
	6: '6_Tiefbohrung',
	7: '7_Moorpunkt',
	70: '9991_Halbtiefbohrung_mit_Protokoll',
	8: '8_GW_hoeher_1',
	9: '9_GW_hoeher_2',
	10: '10_GW_tiefer_1',
	11: '11_GW_tiefer_2',
	12: '12_SW_geringer',
	13: '13_SW_stärker',
	15: '15_Kleingewaesser',
	17: '17_Nährkraft_höher_1',
	18: '18_Nährkraft_höher_2',
	19: '19_Nährkraft_geringer_1',
	20: '20_Nährkraft_geringer_2',
	21: '21_kleinfl_karbonatisch',
	23: '23_kleinfl_organisch',
	24: '24_kleinfl_mineralisch',
	31: '31_nMM',
	32: '32_fMM',
	33: '33_iMM',
	34: '34_mMM',
	35: '35_tMM',
	36: '36_nMo',
	37: '37_fMo',
	38: '38_iMo',
	39: '39_mMo',
	42: '42_fRM',
	43: '43_iRM',
	44: '44_mRM',
	47: '47_fRo',
	48: '48_iRo',
	49: '49_mRo',
	61: '61_iHRM',
	64: '64_iHRo',
	65: '65_mHRo',
	83: '83_stark_verhagerungsgefährdet',
	84: '84_mäßig_verhagerungsgefährdet',
	85: '85_schwach_verhagerungsgefährdet',
	90: '90_GWundNährkraft_höher_1',
	91: '91_GW_höher_1_Nährkraft_höher_2',
	93: '93_GW_tiefer_1_Nährkraft_höher_1',
	94: '94_GW_tiefer_2_Nährkraft_tiefer_1',
	95: '95_GW_tiefer_1_Nährkraft_tiefer_1',
	96: '96_GW_tiefer_1_Nährkraft_tiefer_2',
	97: '97_GW_höher_1_Nährkraft_tiefer_1',
	98: '98_GW_höher_2_Nährkraft_tiefer_1',
	99: '99_GW_tiefer_2_Nährkraft_höher_1',
	100: '100_GW_höher_2_Nährkraft_tiefer_2',
	150: '150_Sonderstandort',
	9990: '9990_Infopunkt',
	9991: '9991_Halbtiefbohrung_mit_Protokoll',
	9998: '9998_Halbtiefbohrung',
	9999: '9999_Grenzaufschluss'
};

export function punktartFromSymbol(symbol: number): Punktart | undefined {
	return SYMBOL_TO_PUNKTART[symbol];
}

export function istPunktartMitProtokoll(punktart: Punktart): boolean {
	return idOf(punktart) === 6 || idOf(punktart) === 7 || idOf(punktart) === 9991 || (idOf(punktart) >=1 && idOf(punktart) <= 4);
}

export function istPunktartMitNummer(punktart: Punktart): boolean {
	return new Set([1, 2, 3, 4, 5, 6, 7, 9990, 9991]).has(idOf(punktart));
}

export function bohrpunktNummerLabel(punktart: Punktart): string {
	if (idOf(punktart) === 9990) return 'Labornummer';
	return 'Nummer';
}

export function punktLabel(bp: AufnahmePunktDocument, useType = false): string {
	const typeDisplayString = (type: string) => type.substring(bp.type.indexOf('_') + 1) + ` (${idOf(bp.type)})`;
	if (istPunktartMitNummer(bp.type)) {
		if (!bp.header.nummer) {
			return useType ? typeDisplayString(bp.type) : '';
		}
		else
			return String(bp.header.nummer);
	}
	else if (useType)
		return typeDisplayString(bp.type);
	return '';
}

export function istPunktartMitKurzprotokoll(punktart: Punktart): boolean {
	// Alle Punktarten ohne volles Protokoll bekommen ein Kurzprotokoll
	return !istPunktartMitProtokoll(punktart);
}

export const isValidNumber = (n: number) => !isNaN(n) && isFinite(n);
export const isValidLatLng = (l: LatLng) => isValidNumber(l.lat) && isValidNumber(l.lng);
export const isValidBounds = (b: LatLngBounds) => isValidLatLng(b.getNorthEast()) && isValidLatLng(b.getSouthWest());

export interface LastModified {
	time: number,
	user: string
}

export interface ImportInfo {
	time: number,              // Timestamp des Imports
	user?: string,             // Nutzer, der den Import gestartet hat
	source?: string,           // Quelldatei (z.B. "LH001_LH030.xlsx / BPF (lh001)")
	coordTransform?: string,   // Koordinaten-Transformation (z.B. "GK Zone 4 → EPSG:5650")
}

export interface AufnahmePunkt {
	version?: number,
	type: Punktart,
	quality: number,
	header: Header,
	extendedHeader: ExtendedHeader,
	Substratfolge: SubstratZeile[];
	Horizontfolge: HorizontZeile[];
	bodenvegetation: BodenVegetation;
	lastModified?: LastModified;
	importInfo?: ImportInfo;
}

export interface Gefuege {
	form: string,
	groesse: string,
	auspraegung: string
}

export interface Feindurchwurzelung {
	dichte: string,
	haeufung: number,
}

export interface SubstratZeile {
	tiefe: number,
	maechtigkeit: number,
	substrat: string,
	wechselschichtung: boolean,
	humusgehalt?: number,
	lagerungsdichte?: number,
	festigkeit?: number,
	skelettgehalt: number,
	kiesgehalt?: number,
	steingehalt?: number,
	karbonatgehalt: number,
	nWsk?: number,
	gefuege?: Gefuege,
	feindurchwurzelung?: Feindurchwurzelung,
	torfarten: string[],
	torfZersetzungsgrad?: number,
	muddeKonsistenz?: number,
	substanzvolumen?: number,
	farbe?: string,
	besonderheiten: string
}

export interface HorizontZeile {
    tiefe: number,
    maechtigkeit: number,
    horizonte: string[]
}

export enum ValidationLevel {
	ok = 0,
	warning = 1,
	danger = 2
};

export interface CheckerComment {
	name: string,
	time: number,
	msg: string
}

export interface CheckerComments {
	resolved: boolean,
	type: ValidationLevel,
	comments: CheckerComment[];
}

export interface ValidationState {
	resolved?: boolean;
	dataSeverity: ValidationLevel;
	commentsSeverity: ValidationLevel;
	hasDiskussions: boolean;
}

export interface AufnahmePunktDocument extends AufnahmePunkt {
	_id: string,
	state: ValidationState;
	checkerComments : Map<string,CheckerComments>;
}

export function roundToString(v?: number, digits = 2): string | undefined {
	if (istLeer(v)) return undefined;
	const pow = Math.pow(10, digits);
	const n = Math.round((v! + Number.EPSILON) * pow) / pow;
	return n.toFixed(digits).replace('.', ',');
}

export function roundToNumber(v?: number, digits = 2): number | undefined {
	if (!v && v !== 0) return;
	const pow = Math.pow(10, digits);
	return Math.round((v! + Number.EPSILON) * pow) / pow;
}

export const punktTemplate = (typ = '6_Tiefbohrung') => {
	const header: Header = {
		nummer: '',
		forstamt: '',
		forstbetrieb: '',
		revier: '',
		adresse: {
			gemarkung: '',
			abteilung: '',
			unterabteilung: '',
			teilflaeche: ''
		},
		koordinaten: {
			system: LocationSystem.EPSG5650,
			x: 0,
			y: 0
		},
		aufnahme: {
			datum: (new Date(Date.now())).toISOString(),
			kartierer: ''
		},
		bodenform: { bf: [{
			trenner: '',
			hbf: '',
			fbf: '',
		}], prefix: '', suffix: ''},
		humusform: {
			aufnahmeFlaeche: { neu: '' }
		},
		vegetationsformengruppe: '',

		substratFeuchte: '',
		bemerkungen: '',
		projects: [],
	};
	const extendedHeader: ExtendedHeader = {
		geologie: {
			oben: '',
			trenner: undefined,
			unten: undefined,
		},
		morphologie: [ '', '' ],
		relief: {
			relief: ''
		},
		humusAuflage: {
		},
		klimaStufe: { neu: '' },
		stfg: {
			stammFeuchteStufe: {
				first: '',
				second: ''
			},
			naehrkraftStufe: '',
			substratGruppe: ''
		},
		baumarten: '',
		entwaesserungsverhalten: '',
		kMgCaPSerie: '', // vielleicht ableitbar aus Bodenform

		nWsk: 0,
		nWskStufe: '',

		mesoklima: '',

		gHorizont: {
			go: '',
			gr: '',
			g: '',
		},
		sHorizont: {
			sw: '',
			sd: '',
			s: '',
		},

		ph: {
			wert1: undefined,
			ort1: '',
			wert2: undefined,
			ort2: '',
			durchschnitt: undefined
		},
	};
	const Substratfolge = new Array<SubstratZeile>();
	const Horizontfolge = new Array<HorizontZeile>();
	const bodenvegetation: BodenVegetation = {/*bestandsBeschreibung: '', */aufnahme: { schichtAnteile: {}, vegetationen: new Array<BodenvegetationZeile>() }};
	return {
		version: CURRENT_VERSION,
		type: typ,
		quality: 1, 
		header: header,
		extendedHeader: extendedHeader,
		Substratfolge: Substratfolge,
		Horizontfolge: Horizontfolge,
		bodenvegetation: bodenvegetation,
	} as AufnahmePunktDocument;
}

export function stfgString(bp: AufnahmePunktDocument) {
    const stfg = bp.extendedHeader.stfg;
    if (!stfg) return '';

    const f1 = stfg.stammFeuchteStufe?.first;
    const nk = stfg.naehrkraftStufe;
    const f2 = stfg.stammFeuchteStufe?.second;

    // Wenn keiner der Hauptwerte vorhanden ist, leere Zelle
    if (!f1 && !nk && !f2 && !stfg.substratGruppe) return '';

		const suff = bp.header.bodenform?.suffix;
		const suffix = suff === '(G)' ? '' : (suff ? suff : '');
		const kst = bp.extendedHeader.klimaStufe?.neu ? `_${bp.extendedHeader.klimaStufe.neu}` : '';
		const sf = bp.header.substratFeuchte ? `_${bp.header.substratFeuchte}` : '';

		// Stammfeuchtestufe nur anzeigen, wenn mindestens ein Teil vorhanden ist
		const stammFeuchte = (f1 || nk || f2) ? `${f1 ?? ''}${nk ?? ''}${f2 ?? ''}` : '';
    return stammFeuchte + (stfg.substratGruppe ? `(${stfg.substratGruppe}${suffix})${sf}${kst}` : '');
  }
